import{useEffect,useState}from'react';
import{
 Alert,Box,Button,Card,CardContent,Chip,CircularProgress,LinearProgress,
 Paper,Stack,Typography,type ButtonProps
}from'@mui/material';
import{useIsFetching,useIsMutating}from'@tanstack/react-query';
import RasanaLogo from'./RasanaLogo';
import{rasanaTokens}from'./theme';

export function PageHeader({
 title,subtitle,action,status
}:{title:string;subtitle?:string;action?:React.ReactNode;status?:React.ReactNode}){
 return <Stack
  direction={{xs:'column',sm:'row'}}
  justifyContent="space-between"
  alignItems={{sm:'flex-start'}}
  spacing={2}
  mb={3}
 >
  <Box>
   <Box sx={{
    width:28,
    height:3,
    borderRadius:2,
    bgcolor:rasanaTokens.gold,
    mb:1.25
   }}/>
   <Typography variant="h4">{title}</Typography>
   {subtitle&&
    <Typography
     color="text.secondary"
     sx={{mt:.5,maxWidth:760,overflowWrap:'anywhere'}}
    >
     {subtitle}
    </Typography>
   }
   {status&&<Box sx={{mt:1.5,display:'flex',flexWrap:'wrap',alignItems:'center',gap:1}}
     aria-label="Status information">{status}</Box>}
  </Box>

  {action&&
   <Box sx={{pt:{sm:.5},width:{xs:'100%',sm:'auto'},
    '& > .MuiButtonBase-root':{width:{xs:'100%',sm:'auto'}}}}>
    {action}
   </Box>
  }
 </Stack>;
}

export function Kpi({label,value,detail}:{
 label:string;
 value:React.ReactNode;
 detail?:React.ReactNode;
}){
 return <Card sx={{
  height:'100%',
  position:'relative',
  overflow:'hidden',
  '&::before':{
   content:'""',
   position:'absolute',
   left:0,
   top:0,
   bottom:0,
   width:3,
   bgcolor:rasanaTokens.primary
  }
 }}>
  <CardContent>
   <Typography
    color="text.secondary"
    variant="body2"
    fontWeight={500}
   >
    {label}
   </Typography>

   <Typography
    sx={{
     mt:.75,
     fontSize:{xs:22,sm:26,md:30},
     overflowWrap:'anywhere',
     lineHeight:1.15,
     fontWeight:600,
     color:rasanaTokens.navy
    }}
   >
    {value}
   </Typography>

   {detail&&
    <Typography
     variant="caption"
     color="text.secondary"
     sx={{display:'block',mt:.75}}
    >
     {detail}
    </Typography>
   }
  </CardContent>
 </Card>;
}

export function Health({value}:{value:string}){
 const color=value==='Green'
  ?'success'
  :value==='Amber'
    ?'warning'
    :value==='Red'
      ?'error'
      :'default';

 return <Chip
  label={value??'Not set'}
  color={color as any}
  size="small"
  variant="filled"
  sx={{
   height:26,
   fontWeight:700,
   cursor:'default',
   pointerEvents:'none',
   bgcolor:value==='Green'?'#E6F3EB':value==='Amber'?'#FFF2D6':
    value==='Red'?'#FCE7E7':'action.hover',
   color:value==='Green'?'#246942':value==='Amber'?'#855C08':
    value==='Red'?'#992D2D':'text.secondary',
   '& .MuiChip-label':{px:1.25}
  }}
 />;
}

export function ErrorBox({error}:{error:unknown}){
 return error
  ?<Alert severity="error" sx={{mb:2}}>
    {error instanceof Error?error.message:String(error)}
   </Alert>
  :null;
}

export function EmptyState({
 title='Nothing here yet',
 description='There is no data to display.'
}:{title?:string;description?:string}){
 return <Paper
  variant="outlined"
  sx={{
   p:{xs:3,md:4},
   textAlign:'center',
   bgcolor:rasanaTokens.ivory
  }}
 >
  <Box sx={{
   display:'flex',
   justifyContent:'center',
   mb:1.5,
   opacity:.72
  }}>
   <RasanaLogo variant="mark" markSize={42}/>
  </Box>

  <Typography variant="subtitle1">{title}</Typography>

  <Typography
   variant="body2"
   color="text.secondary"
   mt={.5}
  >
   {description}
  </Typography>
 </Paper>;
}

export function LoadingState({label='Loading…'}:{label?:string}){
 return <Stack
  direction="row"
  spacing={1.5}
  alignItems="center"
  py={2}
 >
  <CircularProgress size={18} thickness={4}/>
  <Typography variant="body2" color="text.secondary">
   {label}
  </Typography>
 </Stack>;
}

export function BusyButton({
 busy=false,
 busyLabel='Processing…',
 children,
 disabled,
 startIcon,
 ...props
}:ButtonProps&{busy?:boolean;busyLabel?:string}){
 return <Button
  {...props}
  disabled={Boolean(disabled)||busy}
  startIcon={
   busy
    ?<CircularProgress size={16} color="inherit"/>
    :startIcon
  }
 >
  {busy?busyLabel:children}
 </Button>;
}

export function RouteLoadingState(){
 return <Box role="status" aria-live="polite" aria-label="Opening page"
  sx={{py:{xs:5,md:7},maxWidth:1100,mx:'auto'}}>
  <Stack spacing={2.5}>
   <Stack direction="row" spacing={1.5} alignItems="center">
    <CircularProgress size={24} thickness={4}/>
    <Box>
     <Typography variant="subtitle1">Opening your workspace…</Typography>
     <Typography variant="body2" color="text.secondary">
      Preparing this page for you.
     </Typography>
    </Box>
   </Stack>
   <LinearProgress sx={{height:3,borderRadius:2}} aria-label="Page loading"/>
   <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',sm:'repeat(3,1fr)'},gap:2}}>
    {[0,1,2].map(i=><Paper key={i} variant="outlined"
     sx={{height:96,bgcolor:'background.paper',opacity:.6}}/>)}
   </Box>
  </Stack>
 </Box>;
}

export function GlobalActivity(){
 const fetching=useIsFetching();
 const mutating=useIsMutating();
 const active=fetching+mutating;
 const[visible,setVisible]=useState(false);
 useEffect(()=>{
  if(!active){setVisible(false);return;}
  const timer=window.setTimeout(()=>setVisible(true),180);
  return()=>window.clearTimeout(timer);
 },[Boolean(active)]);
 if(!active||!visible)return null;
 return <>
  <LinearProgress role="progressbar" aria-label={mutating?'Saving changes':'Loading data'} sx={{
   position:'fixed',top:0,left:0,right:0,
   zIndex:theme=>theme.zIndex.snackbar+2,height:3
  }}/>
  <Paper elevation={0} role="status" aria-live="polite" sx={{
   position:'fixed',top:12,right:16,
   zIndex:theme=>theme.zIndex.snackbar+1,px:1.5,py:.8,
   pointerEvents:'none',border:`1px solid ${rasanaTokens.borderSoft}`,
   boxShadow:rasanaTokens.shadowSoft
  }}>
   <Stack direction="row" spacing={1} alignItems="center">
    <CircularProgress size={14}/>
    <Typography variant="caption">
     {mutating?'Saving or processing changes…':'Loading information…'}
    </Typography>
   </Stack>
  </Paper>
 </>;
}
